import { Divider, MenuItem, Typography } from "@mui/material";
import useTheme from "@mui/material/styles/useTheme";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  LucideArrowRightFromLine,
  LucideBuilding2,
  LucideLanguages,
  LucideLogOut
} from "lucide-react";
import {
  useMemo,
  useState,
  type Dispatch,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type SetStateAction
} from "react";
import { useTranslation } from "react-i18next";
import { StyledMenu, sectionTitleSx, sectionDividerSx } from "./DropdownMenu.styles";
import chatBotIcon from "../../../assets/chatBotIcon.svg";
import { Link } from "../../../components";
import { PERMISSIONS } from "../../../consts";
import { useAuthStore, useLanguageStore } from "../../../stores";
import { cleanAllCookies } from "../../../utils/cookies";
import { getEnvironmentVariables, getLastPath } from "../../../utils/general";
import { ChatBotIcon, StyledSubHeader } from "../Header.styles";

const { adminLink } = getEnvironmentVariables();

const activateMenuItemOnKey = (e: ReactKeyboardEvent<HTMLLIElement>) => {
  if (e.key === "Enter" || e.key === " ") {
    e.preventDefault();
    e.currentTarget.querySelector<HTMLElement>("a, p")?.click();
  }
};

// Rende cliccabile l'intera riga: i click fuori dal testo (icona, padding) vengono inoltrati all'elemento interno
const activateMenuItemOnClick = (e: ReactMouseEvent<HTMLLIElement>) => {
  const inner = e.currentTarget.querySelector<HTMLElement>("a, p");
  if (inner && !inner.contains(e.target as Node)) inner.click();
};

export function DropdownMenu({
  anchorElDropdown,
  hideMenu,
  isMultilanguage,
  setLanguageModalOpen,
  showChatBot,
  showDropdownMenu,
  showTutorialUserMenu
} : {
  anchorElDropdown: HTMLElement | null
  hideMenu: () => void
  isMultilanguage:boolean
  setLanguageModalOpen:Dispatch<SetStateAction<boolean>>
  showChatBot: () => void
  showDropdownMenu: boolean
  showTutorialUserMenu?: boolean
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const authenticationMode = useAuthStore(state => state.mode);
  const language = useLanguageStore(state => state.language);
  const requestAdminHandoff = useAuthStore(state => state.requestAdminHandoff);
  const session = useAuthStore(state => state.session);
  const sessionToken = useAuthStore(state => state.sessionToken);
  const smartConfiguration = useAuthStore(state => state.smartConfiguration);
  const userData = useAuthStore(state => state.userData);
  const userInfo = useAuthStore(state => state.userInfo);
  const postInitiativeId = useAuthStore(state => state.postInitiativeId);
  const signOut = useAuthStore(state => state.signOut);
  const [handoffPending, setHandoffPending] = useState(false);
  // const localUser = localStorage.getItem("authState");
  // const localUsername = localUser && JSON.parse(localUser || "")?.user.username;
  // const isImpersonation =  !localUsername?.includes(userData?.external_id);
  const isImpersonation = useAuthStore(state => state.isImpersonation);

  const inititiveUrl =  smartConfiguration?.urlDomain
    ? `${smartConfiguration?.urlDomain}.${smartConfiguration.urlCorporateDomain}`
    : smartConfiguration?.urlCorporateDomain
      ?  `${smartConfiguration?.urlCorporateDomain}`
      : window.location.host;

  const initiatives = useMemo(
    () => userInfo?.initiatives?.flatMap((domain) => (
      (domain.id.toString() !== userData?.initiative_id?.toString()) ? [{
        icon: LucideArrowRightFromLine,
        id: domain.id,
        label: domain.label,
        link: "",
        type: "domain"
      }] : []
    )),
    [userData?.initiative_id, userInfo?.initiatives]
  );

  const isAdmin = userData?.permissions?.some(permission => (
    permission === PERMISSIONS["admin.dashboard.access"]
  ));
  const menuItems = getMenuItems();

  function deleteCookie(cname: string) {
    document.cookie =
    cname + "=delete;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/";
  }

  // cleanAllCookie è ora importata da utils/cookies come cleanAllCookies

  return (
    <StyledMenu
      //tabIndex={showDropdownMenu ? 0 : -1}
      id="menu-appbar"
      disableAutoFocusItem
      anchorEl={ anchorElDropdown }
      anchorOrigin={ {
        horizontal: 20,
        vertical: "bottom"
      } }
      classes={ { paper:"scrollable" } }
      className={ "profile-popOver" }
      transformOrigin={ {
        horizontal: "right",
        vertical: -10
      } }
      open={ showDropdownMenu }
      onClose={ hideMenu }
      MenuListProps={ {
        sx: { padding: theme.spacing(1) }
      } }
      sx={ {
        "& .MuiMenuItem-root.Mui-focusVisible": {
          borderRadius: "8px",
          outline: `2px solid ${theme.customColors.systemPrimary01}`,
          outlineOffset: "-2px"
        },
        "& .MuiMenuItem-root:hover": {
          backgroundColor: `rgba(${theme.customColors.backgroundSecondaryHover})`
        },
        "&:focus": {
          outline: "none"
        },
        ".MuiPaper-root": {
          background: theme.customColors.drawerWidgetBg,
          padding: "0.5rem",
          top: showTutorialUserMenu ? "70px !important" : "0",
          width: "14rem"
        }
      } }
    >
      {
        (isMobile && smartConfiguration?.chatbot) ? (
          <MenuItem
            //tabIndex={showDropdownMenu ? 0 : -1}
            //aria-label={ t("chatBot_menu_label") }
            onClick={ ()=> {
              showChatBot();
              hideMenu();
            } }
            sx={ {
              border: showTutorialUserMenu ? `1px solid ${theme.customColors.borderAccent}` : "0",
              borderRadius: showTutorialUserMenu ? "8px" : "0",
              gap: 2
            } }
          >
            <ChatBotIcon
              aria-label={ t("learning_coach") }>
              <img
                alt="chatBot"
                src={ chatBotIcon }
              />
            </ChatBotIcon>
            { t("chatBot_menu_label") }
          </MenuItem>
        ) : null
      }
      {
        initiatives && initiatives.length > 0 && (
          <StyledSubHeader disableSticky role="presentation" sx={ sectionTitleSx }>
            { t("initiatives") }
          </StyledSubHeader>
        )
      }
      {
        initiatives?.map((initiative, index) => {
          const Icon = initiative.icon;

          return (
            <MenuItem
              //tabIndex={showDropdownMenu ? 0 : -1}
              //aria-label={ `${t("switch_to")} ${initiative.label}` }
              key={ index }
              onClick={ activateMenuItemOnClick }
              onKeyDown={ activateMenuItemOnKey }
              sx={ {
                "&.Mui-focusVisible": {
                  borderRadius: "8px",
                  outline: `2px solid ${theme.palette.text?.primary}`,
                  outlineOffset: "-2px"
                },
                ">a, p": {
                  alignItems: "center",
                  color: theme.customColors.exploreCardText,
                  display: "flex",
                  flexWrap: "wrap",
                  fontSize: "0.75rem",
                  fontWeight: 400,
                  gap: 0.5,
                  lineHeight: "1rem",
                  textAlign: "start",
                  textDecoration: "none",
                  wordBreak: "break-word"
                },
                borderRadius: "0.25rem",
                gap: "0.5rem",
                margin: 0,
                padding: theme.spacing(1, 0.5),
                textTransform: "capitalize",
                whiteSpace: "normal"
              } }>
              <Icon
                aria-hidden="true"
                color={ theme.customColors.exploreCardText }
                size={ "0.875rem" }
              />
              <Typography
                tabIndex={ 0 }
                aria-label={ `${t("switch_to")} ${initiative.label}` }
                textAlign="center"
                onClick={ (e) => {
                  e.preventDefault();
                  hideMenu();
                  if (
                    initiative.id
                    && session
                    && sessionToken
                    && userData?.organization_id
                  ) {

                    //Clean older cookies
                    document.cookie.split(";").forEach((cookie) => {
                      const cookieName = cookie.split("=")[0].trim();
                      if (cookieName.startsWith("tutorial-completed")) {
                        const domain = getLastPath();
                        document.cookie =
                          `${cookieName}=;secure; expires=Thu, 01 Jan 1970 00:00:00 UTC; domain=.${domain}; path=/`;
                      }
                    });

                    postInitiativeId(initiative.id);
                  }
                } }
              >
                { `${t("switch_to")} ${initiative.label}` }
              </Typography>
            </MenuItem>
          );
        })
      }
      {
        menuItems.map((menuItem, index) => {

          const Icon = menuItem.icon;

          const hasContentAboveLanguage =
            (isMobile && Boolean(smartConfiguration?.chatbot)) ||
            Boolean(initiatives && initiatives.length > 0) ||
            index > 0;

          return [
            ...(menuItem.label === "logout" ?
              [<Divider key={ `lang-divider-${index}` } sx={ sectionDividerSx } />]
              : menuItem.label === "language"
                ? [
                  ...(hasContentAboveLanguage
                    ? [<Divider key={ `lang-divider-${index}` } sx={ sectionDividerSx } />]
                    : []),
                  <StyledSubHeader
                    disableSticky
                    role="presentation"
                    key={ `lang-title-${index}` }
                    sx={ sectionTitleSx }
                  >
                    { t("default_language") }
                  </StyledSubHeader>
                ]
                : []),
            <MenuItem
              key={ index }
              //tabIndex={showDropdownMenu ? 0 : -1}
              //aria-label={ t(menuItem.label) }
              onClick={ activateMenuItemOnClick }
              onKeyDown={ activateMenuItemOnKey }
              sx={ {
                "&.Mui-focusVisible": {
                  borderRadius: "8px",
                  outline: `2px solid ${theme.palette.text?.primary}`,
                  outlineOffset: "-2px"
                },
                ">a, p": {
                  alignItems: "center",
                  color: theme.customColors.exploreCardText,
                  display: "flex",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  gap: "0.5rem",
                  textAlign: "start",
                  textDecoration: "none"
                },
                borderRadius: "0.25rem",
                gap: "0.5rem",
                margin: 0,
                padding: theme.spacing(1, 0.5),
                textTransform: "capitalize",
                whiteSpace: "normal"
              } }
            >
              {
                menuItem.label === "logout" ? (
                  <Typography
                    tabIndex={ 0 }
                    aria-label={ t(menuItem.label) }
                    textAlign="center"
                    onClick={ (e) => {
                      e.preventDefault();
                      cleanAllCookies(["languageStored"]); // Usa la funzione centralizzata
                      hideMenu();
                      signOut();
                    } }
                  > <Icon aria-hidden="true" color={ theme.customColors.exploreCardText } size={ "0.875rem" }/>
                    { t(menuItem.label) }
                  </Typography>

                ) : (menuItem.label === "language") ? (
                  <>
                    { language && (
                      <Typography
                        tabIndex={ 0 }
                        aria-label={ `${ language.label} (${language.id?.toUpperCase()})` }
                        textAlign="center"
                        onClick={ (e) => {
                          e.preventDefault();
                          hideMenu();
                          setLanguageModalOpen(true);
                        } }
                      >
                        <Icon aria-hidden="true" color={ theme.customColors.exploreCardText } size={ "0.875rem" }/>
                        { `${ language.label} (${language.id?.toUpperCase()})` }
                      </Typography>
                    ) }
                  </>
                ) :
                  (
                    <>
                      <Icon aria-hidden="true" color={ theme.customColors.exploreCardText } size={ "0.875rem" }/>
                      <Link
                        tabIndex={ 0 }
                        href={ menuItem.label === "switch_admin" ? undefined : menuItem.link }
                        aria-disabled={ menuItem.label === "switch_admin" && handoffPending }
                        onClick={ async (e) => {
                          if (menuItem.label === "switch_admin") {
                            e.preventDefault();
                            if (handoffPending) return;
                            setHandoffPending(true);
                            try {
                              const token = await requestAdminHandoff();
                              deleteCookie("languageStored");
                              // eslint-disable-next-line max-len
                              window.location.href = `${adminLink}?mode=${authenticationMode}&initiativeUrl=${inititiveUrl}#handoff=${token}`;
                            } catch (error) {
                            // eslint-disable-next-line no-console
                              console.error("switch_admin handoff failed", error);
                              setHandoffPending(false);
                            }
                          }
                        } }
                        target={ menuItem.target }
                        textAlign="center"
                        rel={ "noreferrer" }
                        sx={ {
                          fontWeight: "400 !important",
                          ...(menuItem.label === "switch_admin" && handoffPending
                            ? { opacity: 0.5, pointerEvents: "none" }
                            : {})
                        } }
                      >

                        { t(menuItem.label) }
                      </Link>
                    </>
                  )
              }
            </MenuItem>
          ];
        })
      }
    </StyledMenu>
  );

  function getMenuItems() {
    const menuItems = [];

    const admin = {
      icon: LucideBuilding2,
      label: "switch_admin",
      link: "",
      // TODO impostando initiativeUrl quando passiamo ad admin se poi da admin impersonifichiamo un utente di
      //di una corporate diversa veniamo portati alla pagina portal con i dati corretti ma con l'url sbagliato
      //ovvero con l'url dell'iniziativa da cui abbiamo fatto switch e non con l'url
      // dell'iniziativa/corporate dell'utente impersonificato
      target: "_self",
      type: "link"
    };

    const multiLanguage = {
      icon: LucideLanguages,
      label: "language",
      link: "",
      target: "_self",
      type: "link"
    };

    const logOut = {
      icon: LucideLogOut,
      label: "logout",
      link: "",
      target: "_self",
      type: "link"
    };

    if (isAdmin && !isImpersonation) { menuItems.push(admin) }
    if (isMultilanguage) { menuItems.push(multiLanguage)}
    menuItems.push(logOut);

    return menuItems;
  }
}
